/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Home, 
  DollarSign, 
  Wrench, 
  CreditCard,
  Calendar,
  Settings,
  LogOut,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Download,
  FileText,
  HelpCircle
} from 'lucide-react';
import StripeAutopayEnrollment from './tenant/StripeAutopayEnrollment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TenantDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [newMaintenanceRequest, setNewMaintenanceRequest] = useState({
    title: '',
    description: '',
    priority: 'medium'
  });
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Determine if user is HOA owner or tenant based on their role AND current path
  const [isHOA, setIsHOA] = useState(() => {
    const userRole = localStorage.getItem('user_role');
    const isHOAPath = window.location.pathname.includes('hoa-owner');
    return userRole === 'hoa_owner' || isHOAPath;
  });
  const [orgType, setOrgType] = useState(null);
  
  useEffect(() => {
    // Check user role and path to determine if HOA
    const userRole = localStorage.getItem('user_role');
    const isHOAPath = window.location.pathname.includes('hoa-owner');
    console.log('DEBUG: userRole =', userRole, ', isHOAPath =', isHOAPath, ', setting isHOA to:', userRole === 'hoa_owner' || isHOAPath);
    setIsHOA(userRole === 'hoa_owner' || isHOAPath);
    
    const checkOrgType = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.organization) {
          setOrgType(response.data.organization.type);
        }
      } catch (error) {
        console.error('Error checking org type:', error);
      }
    };
    checkOrgType();
  }, []);
  
  const paymentLabel = isHOA ? 'HOA Fees' : 'Rent';
  const paymentAction = isHOA ? 'Pay HOA Dues' : 'Pay Rent';
  const portalTitle = isHOA ? 'Owner Portal' : 'Tenant Dashboard';

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userRole = localStorage.getItem('user_role');
    
    // Allow both tenant and hoa_owner roles to access this dashboard
    if (!token || (userRole !== 'tenant' && userRole !== 'hoa_owner')) {
      // Redirect based on current path
      if (window.location.pathname.includes('hoa-owner')) {
        navigate('/hoa-owner/login');
      } else {
        navigate('/tenant/login');
      }
      return;
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const token = localStorage.getItem('access_token');
      const userRole = localStorage.getItem('user_role');
      const isHOAPath = window.location.pathname.includes('hoa-owner');
      
      // Use different endpoint based on role or path
      const endpoint = (userRole === 'hoa_owner' || isHOAPath) 
        ? '/hoa-owner/dashboard' 
        : `/tenant/dashboard/${userId}`;
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMaintenanceRequest = async (e) => {
    e.preventDefault();
    
    if (!newMaintenanceRequest.title || !newMaintenanceRequest.description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const userId = localStorage.getItem('user_id');
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/tenant/${userId}/maintenance-request`, newMaintenanceRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Maintenance request submitted successfully!');
      setNewMaintenanceRequest({ title: '', description: '', priority: 'medium' });
      setShowMaintenanceDialog(false);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit maintenance request');
    }
  };

  const handlePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      setProcessingPayment(true);
      const userId = localStorage.getItem('user_id');
      const token = localStorage.getItem('access_token');
      
      // Get current origin for Stripe redirect URLs
      const origin = window.location.origin;
      
      // Create Stripe checkout session
      const response = await axios.post(
        `${API}/payments/stripe/create-checkout-session`,
        {
          amount: parseFloat(paymentAmount),
          currency: 'usd',
          payment_type: isHOA ? 'hoa_dues' : 'rent',
          user_id: userId
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Origin': origin
          }
        }
      );
      
      if (response.data.success && response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    try {
      // Generate receipt content
      const receiptContent = `
ESTATE FLOW
Payment Receipt
=====================================

Receipt ID: ${payment.id || payment._id || 'N/A'}
Order ID: ${payment.order_id || payment.session_id || 'N/A'}
Date: ${payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : 'N/A'}

=====================================
PAYMENT DETAILS
=====================================

Payment Type: ${payment.payment_type === 'rent' ? 'Rent Payment' : 'HOA Dues'}
Amount Paid: $${payment.amount?.toFixed(2) || '0.00'}
Payment Status: ${payment.payment_status || payment.status || 'N/A'}
Payment Method: ${payment.processor || 'Card'}

=====================================
TRANSACTION INFORMATION
=====================================

Transaction ID: ${payment.transaction_id || 'N/A'}
Processor: ${payment.processor || 'PlugnPay'}

=====================================

Thank you for your payment!

Estate Flow - Property Management Platform
Copyright © 2025 Sebastian Alliance Group LLC
All Rights Reserved

=====================================
      `;

      // Create and download file
      const blob = new Blob([receiptContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EstateFlow_Receipt_${payment.order_id || payment.id || 'receipt'}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    }
  };
  
  // Check for payment return from Stripe
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        try {
          const token = localStorage.getItem('access_token');
          const response = await axios.get(
            `${API}/payments/stripe/checkout-status/${sessionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.payment_status === 'paid') {
            toast.success('Payment successful! Thank you.');
            // Remove session_id from URL
            window.history.replaceState({}, '', window.location.pathname);
            fetchDashboardData();
            fetchPaymentHistory();
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      }
    };
    
    checkPaymentStatus();
  }, []);
  
  // Fetch payment history
  const fetchPaymentHistory = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${API}/payments/history/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPaymentHistory(response.data.payments);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };
  
  // Load payment history on mount
  useEffect(() => {
    if (!loading) {
      fetchPaymentHistory();
    }
  }, [loading]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    navigate('/tenant/login');
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Building2 className="h-8 w-8 text-emerald-600" />
              <div>
                <span className="text-2xl font-bold text-gray-900">Estate Flow</span>
                <p className="text-xs text-gray-500 -mt-1">{isHOA ? 'Owner Portal' : 'Tenant Portal'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                <span>Welcome, {dashboardData?.tenant?.first_name}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/kb')}
                data-testid="tenant-dashboard-help-btn"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Help
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
                data-testid="tenant-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isHOA ? 'Welcome to Your Owner Portal' : 'Welcome to Your Rental Portal'}
          </h1>
          <p className="text-gray-600">
            {dashboardData?.unit && dashboardData?.property ? 
              `Unit ${dashboardData.unit.unit_number} - ${dashboardData.property.name}` :
              isHOA ? 'Your property information' : 'Your rental information'
            }
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="rent-amount-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly {paymentLabel}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${dashboardData?.unit?.rent_amount ? dashboardData.unit.rent_amount.toFixed(2) : '0.00'}
              </div>
              <p className="text-xs text-muted-foreground">Due on the 1st of each month</p>
            </CardContent>
          </Card>
          
          <Card data-testid="payments-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recent Payments</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardData?.recent_payments?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">Payments this year</p>
            </CardContent>
          </Card>
          
          <Card data-testid="maintenance-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Maintenance Requests</CardTitle>
              <Wrench className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardData?.maintenance_requests?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">Active requests</p>
            </CardContent>
          </Card>
          
          <Card data-testid="property-info-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Property</CardTitle>
              <Home className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold">
                Unit {dashboardData?.unit?.unit_number || 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {dashboardData?.property?.name || 'Property name'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                {paymentAction}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                {isHOA ? 'Pay your HOA dues securely online with instant confirmation' : 'Pay your rent securely online with instant confirmation'}
              </p>
              <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="make-payment-btn">
                    <DollarSign className="mr-2 h-4 w-4" />
                    {paymentAction}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{paymentAction}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment-amount">Payment Amount (USD)</Label>
                      <Input
                        id="payment-amount"
                        type="number"
                        step="0.01"
                        placeholder="Enter amount"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <Button 
                      onClick={handlePayment} 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={processingPayment}
                    >
                      {processingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="mr-2 h-4 w-4" />
                          Proceed to Secure Checkout
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      You will be redirected to our secure payment gateway
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="h-5 w-5 mr-2" />
                Maintenance Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Report issues or request maintenance for your unit
              </p>
              <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="maintenance-request-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Submit Request
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Submit Maintenance Request</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitMaintenanceRequest} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="request-title">Request Title</Label>
                      <Input
                        id="request-title"
                        placeholder="Brief description of the issue"
                        value={newMaintenanceRequest.title}
                        onChange={(e) => setNewMaintenanceRequest({...newMaintenanceRequest, title: e.target.value})}
                        data-testid="maintenance-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="request-description">Description</Label>
                      <Textarea
                        id="request-description"
                        placeholder="Detailed description of the issue"
                        rows={4}
                        value={newMaintenanceRequest.description}
                        onChange={(e) => setNewMaintenanceRequest({...newMaintenanceRequest, description: e.target.value})}
                        data-testid="maintenance-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Priority Level</Label>
                      <Select 
                        value={newMaintenanceRequest.priority} 
                        onValueChange={(value) => setNewMaintenanceRequest({...newMaintenanceRequest, priority: value})}
                      >
                        <SelectTrigger data-testid="maintenance-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - Can wait</SelectItem>
                          <SelectItem value="medium">Medium - Normal</SelectItem>
                          <SelectItem value="high">High - Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                      Submit Request
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paymentHistory.length > 0 ? (
                <div className="space-y-4">
                  {paymentHistory.slice(0, 5).map((payment, index) => (
                    <div key={payment._id || index} className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex-1">
                        <p className="font-semibold text-lg">${payment.amount?.toFixed(2) || '0.00'}</p>
                        <p className="text-sm text-gray-600">
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          }) : 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {payment.payment_type === 'rent' ? 'Rent Payment' : 'HOA Dues'}
                        </p>
                        {payment.order_id && (
                          <p className="text-xs text-gray-400 mt-1 font-mono">
                            Order: {payment.order_id.substring(0, 16)}
                          </p>
                        )}
                      </div>
                      <div className="text-right flex flex-col items-end space-y-2">
                        <Badge className={
                          payment.payment_status === 'paid' || payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.payment_status === 'pending' || payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'refunded' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {payment.payment_status === 'paid' || payment.status === 'completed' ? 'Paid' : 
                           payment.payment_status === 'pending' || payment.status === 'pending' ? 'Pending' : 
                           payment.status === 'refunded' ? 'Refunded' : 'Failed'}
                        </Badge>
                        {(payment.payment_status === 'paid' || payment.status === 'completed') && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs"
                            onClick={() => handleDownloadReceipt(payment)}
                          >
                            Download Receipt
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button 
                    variant="link" 
                    className="w-full mt-4"
                    onClick={() => setShowPaymentDialog(true)}
                  >
                    View All Payment History →
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No payments yet</p>
                  <p className="text-sm mt-2">Make your first payment using the button above</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Automatic Payments (Stripe Subscriptions) */}
          <StripeAutopayEnrollment 
            tenantData={{
              property_id: dashboardData?.property?.id,
              unit_id: dashboardData?.unit?.id
            }}
            onEnrollmentChange={fetchDashboardData}
          />

          {/* Maintenance Requests */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="h-5 w-5 mr-2" />
                Maintenance Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.maintenance_requests?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.maintenance_requests.slice(0, 5).map((request, index) => (
                    <div key={request.id || index} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium">{request.title}</h4>
                        <Badge className={getPriorityColor(request.priority)}>
                          {request.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{request.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                        <Badge className={getStatusColor(request.status)}>
                          {request.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Wrench className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No maintenance requests</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TenantDashboard;
